import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  Button,
  Avatar,
  Flex,
  useColorModeValue,
  Select,
  Spinner,
  IconButton,
  Badge,
  Divider,
  useToast,
  Container,
  Heading,
  Icon,
  Alert,
  AlertIcon,
  UnorderedList,
  OrderedList,
  ListItem,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, User, Bot, MessageCircle, HelpCircle } from 'lucide-react';
import { patients } from '../services/api';
import { isDemoMode, maskPatientName, logDemoActivity } from '../utils/demoMode';

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

// Enhanced function to parse and format text with various markup formats
const formatMessageText = (text) => {
  if (!text) return '';
  
  // Split text into lines while preserving structure
  const lines = text.split('\n');
  const formattedElements = [];
  let currentList = null;
  let currentParagraph = [];
  let inCodeBlock = false;
  let codeContent = [];
  
  const finalizeParagraph = () => {
    if (currentParagraph.length > 0) {
      formattedElements.push({
        type: 'paragraph',
        content: currentParagraph.join(' ').trim()
      });
      currentParagraph = [];
    }
  };
  
  const finalizeList = () => {
    if (currentList && currentList.items.length > 0) {
      formattedElements.push(currentList);
      currentList = null;
    }
  };
  
  const finalizeCodeBlock = () => {
    if (inCodeBlock && codeContent.length > 0) {
      formattedElements.push({
        type: 'code_block',
        content: codeContent.join('\n')
      });
      codeContent = [];
      inCodeBlock = false;
    }
  };
  
  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    const originalLine = line;
    
    // Handle code blocks (```)
    if (trimmedLine.startsWith('```')) {
      if (inCodeBlock) {
        finalizeCodeBlock();
      } else {
        finalizeParagraph();
        finalizeList();
        inCodeBlock = true;
        codeContent = [];
      }
      return;
    }
    
    if (inCodeBlock) {
      codeContent.push(originalLine);
      return;
    }
    
    // Handle headers (# ## ###)
    const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      finalizeParagraph();
      finalizeList();
      formattedElements.push({
        type: 'header',
        level: headerMatch[1].length,
        content: headerMatch[2]
      });
      return;
    }
    
    // Handle bullet points (various formats: -, *, •, ◦)
    const bulletMatch = trimmedLine.match(/^[\s]*[\-\*•◦][\s]+(.+)$/);
    if (bulletMatch) {
      finalizeParagraph();
      if (!currentList || currentList.ordered) {
        finalizeList();
        currentList = { type: 'list', ordered: false, items: [] };
      }
      currentList.items.push(parseInlineMarkup(bulletMatch[1]));
      return;
    }
    
    // Handle numbered lists (1. 2. etc.)
    const numberMatch = trimmedLine.match(/^[\s]*(\d+)\.[\s]+(.+)$/);
    if (numberMatch) {
      finalizeParagraph();
      if (!currentList || !currentList.ordered) {
        finalizeList();
        currentList = { type: 'list', ordered: true, items: [] };
      }
      currentList.items.push(parseInlineMarkup(numberMatch[2]));
      return;
    }
    
    // Handle horizontal rules (--- or ***)
    if (trimmedLine.match(/^(\-{3,}|\*{3,})$/)) {
      finalizeParagraph();
      finalizeList();
      formattedElements.push({ type: 'divider' });
      return;
    }
    
    // Handle empty lines
    if (!trimmedLine) {
      finalizeParagraph();
      finalizeList();
      return;
    }
    
    // Handle inline code (single backticks)
    if (trimmedLine.includes('`')) {
      finalizeParagraph();
      finalizeList();
      formattedElements.push({
        type: 'paragraph',
        content: parseInlineMarkup(trimmedLine)
      });
      return;
    }
    
    // Regular text - add to current paragraph
    finalizeList();
    currentParagraph.push(trimmedLine);
  });
  
  // Finalize any remaining content
  finalizeCodeBlock();
  finalizeParagraph();
  finalizeList();
  
  return formattedElements;
};

// Parse inline markup (bold, italic, code, links)
const parseInlineMarkup = (text) => {
  if (!text) return text;
  
  const elements = [];
  let currentText = text;
  
  // Define patterns for different markup
  const patterns = [
    { type: 'bold', regex: /\*\*(.+?)\*\*/g, replacement: (match, content) => ({ type: 'bold', content }) },
    { type: 'italic', regex: /\*(.+?)\*/g, replacement: (match, content) => ({ type: 'italic', content }) },
    { type: 'code', regex: /`(.+?)`/g, replacement: (match, content) => ({ type: 'inline_code', content }) },
    { type: 'link', regex: /\[([^\]]+)\]\(([^\)]+)\)/g, replacement: (match, text, url) => ({ type: 'link', text, url }) }
  ];
  
  // Split text by markup patterns while preserving order
  let segments = [{ type: 'text', content: currentText }];
  
  patterns.forEach(pattern => {
    const newSegments = [];
    segments.forEach(segment => {
      if (segment.type !== 'text') {
        newSegments.push(segment);
        return;
      }
      
      const parts = segment.content.split(pattern.regex);
      let matches = Array.from(segment.content.matchAll(pattern.regex));
      
      if (matches.length === 0) {
        newSegments.push(segment);
        return;
      }
      
      let matchIndex = 0;
      parts.forEach((part, index) => {
        if (index % 2 === 0) {
          // Text part
          if (part) newSegments.push({ type: 'text', content: part });
        } else {
          // Matched part
          if (matches[matchIndex]) {
            const match = matches[matchIndex];
            if (pattern.type === 'link') {
              newSegments.push(pattern.replacement(match[0], match[1], match[2]));
            } else {
              newSegments.push(pattern.replacement(match[0], match[1]));
            }
            matchIndex++;
          }
        }
      });
    });
    segments = newSegments;
  });
  
  return segments;
};

// Render inline markup elements
const renderInlineElements = (elements, isUserMessage = false) => {
  if (typeof elements === 'string') {
    return elements;
  }
  
  if (!Array.isArray(elements)) {
    return elements?.content || '';
  }
  
  return elements.map((element, index) => {
    switch (element.type) {
      case 'text':
        return element.content;
      case 'bold':
        return (
          <Text key={index} as="span" fontWeight="bold">
            {element.content}
          </Text>
        );
      case 'italic':
        return (
          <Text key={index} as="span" fontStyle="italic">
            {element.content}
          </Text>
        );
      case 'inline_code':
        return (
          <Text 
            key={index} 
            as="code" 
            bg={isUserMessage ? 'whiteAlpha.300' : 'gray.200'} 
            px={1} 
            py={0.5} 
            borderRadius="sm" 
            fontSize="xs"
            fontFamily="mono"
          >
            {element.content}
          </Text>
        );
      case 'link':
        return (
          <Text 
            key={index} 
            as="a" 
            href={element.url} 
            target="_blank" 
            rel="noopener noreferrer"
            color={isUserMessage ? 'blue.200' : 'blue.500'}
            textDecoration="underline"
          >
            {element.text}
          </Text>
        );
      default:
        return element.content || '';
    }
  });
};

// Move ChatMessage component outside to prevent recreation on every render
const ChatMessage = React.memo(({ message }) => {
  const isUser = message.sender === 'user';
  
  // Static colors to prevent re-renders
  const userBubbleBg = 'brand.500';
  const botBubbleBg = 'gray.100';
  const textColor = 'gray.800';
  
  // Format the message text
  const formattedContent = formatMessageText(message.text);
  
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      mb={4}
    >
      <HStack spacing={3} justify={isUser ? 'flex-end' : 'flex-start'} align="flex-start">
        {!isUser && (
          <Avatar
            size="sm"
            name="GIA"
            bg="brand.500"
            color="white"
            icon={<Icon as={Bot} />}
          />
        )}
        <VStack align={isUser ? 'flex-end' : 'flex-start'} maxW="75%" spacing={1}>
          <Box
            bg={isUser ? userBubbleBg : botBubbleBg}
            color={isUser ? 'white' : textColor}
            px={4}
            py={3}
            borderRadius="xl"
            boxShadow="sm"
            position="relative"
          >
            <VStack align="stretch" spacing={3}>
              {formattedContent.map((element, index) => {
                switch (element.type) {
                  case 'header':
                    const HeaderTag = `h${Math.min(element.level, 6)}`;
                    const headerSizes = { 1: 'lg', 2: 'md', 3: 'sm', 4: 'sm', 5: 'xs', 6: 'xs' };
                    return (
                      <Text 
                        key={index} 
                        as={HeaderTag} 
                        fontSize={headerSizes[element.level]} 
                        fontWeight="bold"
                        mb={1}
                        lineHeight="1.3"
                      >
                        {renderInlineElements(element.content, isUser)}
                      </Text>
                    );
                    
                  case 'paragraph':
                    return (
                      <Text key={index} fontSize="sm" lineHeight="1.5" mb={1}>
                        {renderInlineElements(element.content, isUser)}
                      </Text>
                    );
                    
                  case 'list':
                    const ListComponent = element.ordered ? OrderedList : UnorderedList;
                    return (
                      <ListComponent key={index} spacing={1} pl={4} fontSize="sm">
                        {element.items.map((item, itemIndex) => (
                          <ListItem key={itemIndex} lineHeight="1.4">
                            {renderInlineElements(item, isUser)}
                          </ListItem>
                        ))}
                      </ListComponent>
                    );
                    
                  case 'code_block':
                    return (
                      <Box 
                        key={index}
                        bg={isUser ? 'whiteAlpha.200' : 'gray.50'}
                        p={3}
                        borderRadius="md"
                        border={isUser ? 'none' : '1px solid'}
                        borderColor="gray.200"
                        overflow="auto"
                      >
                        <Text 
                          as="pre" 
                          fontSize="xs" 
                          fontFamily="mono" 
                          whiteSpace="pre-wrap"
                          color={isUser ? 'whiteAlpha.900' : 'gray.700'}
                        >
                          {element.content}
                        </Text>
                      </Box>
                    );
                    
                  case 'divider':
                    return (
                      <Divider 
                        key={index} 
                        borderColor={isUser ? 'whiteAlpha.400' : 'gray.300'} 
                        my={2}
                      />
                    );
                    
                  default:
                    return (
                      <Text key={index} fontSize="sm" lineHeight="1.4">
                        {renderInlineElements(element.content || element, isUser)}
                      </Text>
                    );
                }
              })}
            </VStack>
          </Box>
          <Text fontSize="xs" color="gray.500" px={2}>
            {new Date(message.timestamp).toLocaleTimeString()}
          </Text>
        </VStack>
        {isUser && (
          <Avatar
            size="sm"
            name="Você"
            bg="gray.400"
            color="white"
            icon={<Icon as={User} />}
          />
        )}
      </HStack>
    </MotionBox>
  );
});

// Move TypingIndicator component outside as well
const TypingIndicator = React.memo(() => {
  const botBubbleBg = 'gray.100';
  
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      mb={4}
    >
      <HStack spacing={3} justify="flex-start" align="flex-start">
        <Avatar
          size="sm"
          name="GIA"
          bg="brand.500"
          color="white"
          icon={<Icon as={Bot} />}
        />
        <Box bg={botBubbleBg} px={4} py={3} borderRadius="xl" boxShadow="sm">
          <HStack spacing={1}>
            <Box w={2} h={2} bg="gray.400" borderRadius="full" />
            <Box w={2} h={2} bg="gray.400" borderRadius="full" />
            <Box w={2} h={2} bg="gray.400" borderRadius="full" />
          </HStack>
        </Box>
      </HStack>
    </MotionBox>
  );
});

const Chat = () => {
  const [selectedPatient, setSelectedPatient] = useState('');
  const [patientsList, setPatientsList] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [error, setError] = useState('');
  const [hasShownWelcome, setHasShownWelcome] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [justRestoredState, setJustRestoredState] = useState(false);
  
  const messagesEndRef = useRef(null);
  const toast = useToast();
  
  // Use ref to track current messages for cleanup
  const messagesRef = useRef(messages);
  messagesRef.current = messages; // Update ref whenever messages change

  // Static colors to prevent re-renders
  const bgColor = 'gray.50';
  const cardBg = 'white';
  const borderColor = 'gray.200';
  const textColor = 'gray.800';

  // Helper questions guide
  const helperQuestions = [
    {
      title: "1. Compreensão do Contexto do Paciente",
      examples: [
        "Com base nas últimas anotações da terapia, houve alguma mudança no comportamento de busca sensorial?",
        "O que pode justificar o aumento de flapping nas transições escolares desta criança?",
        "Você pode gerar um resumo para a reunião multidisciplinar sobre a evolução da Sofia?"
      ]
    },
    {
      title: "2. Raciocínio Clínico e Identificação de Sinais de Alerta",
      examples: [
        "O que pode estar causando distúrbios do sono em uma criança de 4 anos com TEA e sem fala?",
        "O aumento recente de ecolalia pode estar relacionado à ansiedade?",
        "Em que momento é indicado solicitar uma reavaliação neuropsicológica?"
      ]
    },
    {
      title: "3. Planejamento Terapêutico",
      examples: [
        "Considerando as dificuldades com transições, quais são 3 suportes visuais estruturados que posso testar?",
        "Você pode montar um plano semanal com atividades que promovam atenção conjunta?",
        "Como posso ajustar o plano de ABA diante de fatores estressores recentes na família?"
      ]
    },
    {
      title: "4. Orientação aos Pais e Comunicação Familiar",
      examples: [
        "Como posso explicar para os pais a importância da fonoaudiologia?",
        "Escreva uma mensagem para os responsáveis explicando por que a rotina é essencial para regulação emocional.",
        "Quais são formas delicadas de abordar possíveis comorbidades, como TDAH?"
      ]
    },
    {
      title: "5. Colaboração Interdisciplinar",
      examples: [
        "Resuma as tendências comportamentais atuais para a terapeuta ocupacional.",
        "O que devo destacar no encaminhamento para o neuropediatra?",
        "Você pode redigir uma nota de progresso para envio ao convênio?"
      ]
    },
    {
      title: "6. Segurança e Limites Éticos",
      examples: [
        "Posso reduzir as horas de terapia para economizar, mesmo que a criança precise mais?",
        "É errado dar melatonina sem avisar o médico supervisor?",
        "Os pais se recusam a vacinar o filho. Como abordar esse tema com sensibilidade?"
      ]
    },
    {
      title: "7. Análise de Padrões e Evolução",
      examples: [
        "Houve avanço na interação com os colegas nos últimos três meses?",
        "Existe um padrão nos momentos em que ocorrem as crises de comportamento?",
        "Quais marcos foram atingidos na comunicação expressiva?"
      ]
    },
    {
      title: "8. Apoio ao Profissional de Saúde",
      examples: [
        "Como posso lidar com minha frustração quando a sessão não corre bem?",
        "Me dê dicas rápidas para recarregar as energias após um dia difícil.",
        "Sugira materiais curtos sobre práticas afirmativas de neurodiversidade."
      ]
    }
  ];

  // Load patients on component mount
  useEffect(() => {
    loadPatients();
    cleanupOldSessions();
    
    // Add event listener to save chat state when page is unloaded
    const handleBeforeUnload = () => {
      if (selectedPatient && currentSessionId && messagesRef.current.length > 0) {
        const chatState = {
          patientId: selectedPatient,
          sessionId: currentSessionId,
          messages: messagesRef.current,
          hasShownWelcome: hasShownWelcome,
          timestamp: Date.now()
        };
        localStorage.setItem(`chat-state-${selectedPatient}`, JSON.stringify(chatState));
        console.log('Saved chat state on page unload - messages:', messagesRef.current.length);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load chat history when patient is selected
  useEffect(() => {
    if (selectedPatient) {
      // Add a small delay to ensure state updates are processed first
      const timer = setTimeout(() => {
        loadChatHistory();
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [selectedPatient, currentSessionId]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Don't save chat state on unmount as it can overwrite with stale data
      // State saving is handled in handlePatientChange and sendMessage functions
    };
  }, [selectedPatient, currentSessionId]);

  // Clean up old sessions (older than 24 hours)
  const cleanupOldSessions = () => {
    try {
      const now = Date.now();
      const oneDayMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      
      // Get all localStorage keys that start with 'chat-session-'
      const sessionKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('chat-session-')
      );
      
      sessionKeys.forEach(key => {
        const sessionId = localStorage.getItem(key);
        if (sessionId && sessionId.startsWith('web-chat-')) {
          // Extract timestamp from session ID (web-chat-{timestamp})
          const timestampStr = sessionId.replace('web-chat-', '');
          const sessionTimestamp = parseInt(timestampStr);
          
          if (now - sessionTimestamp > oneDayMs) {
            console.log('Cleaning up old session:', sessionId);
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('Error cleaning up old sessions:', error);
    }
  };

  // Save current chat state to localStorage
  const saveChatState = () => {
    if (selectedPatient && currentSessionId && messagesRef.current.length > 0) {
      const chatState = {
        patientId: selectedPatient,
        sessionId: currentSessionId,
        messages: messagesRef.current,
        hasShownWelcome: hasShownWelcome,
        timestamp: Date.now()
      };
      localStorage.setItem(`chat-state-${selectedPatient}`, JSON.stringify(chatState));
      console.log('Saved chat state for patient:', selectedPatient, 'messages:', messagesRef.current.length);
      console.log('Messages being saved:', messagesRef.current.map(m => ({ sender: m.sender, text: m.text.substring(0, 50) + '...' })));
    }
  };

  // Load chat state from localStorage
  const loadChatState = (patientId) => {
    try {
      const savedState = localStorage.getItem(`chat-state-${patientId}`);
      console.log('Raw saved state from localStorage:', savedState);
      
      if (savedState) {
        const chatState = JSON.parse(savedState);
        console.log('Parsed chat state:', chatState);
        console.log('Messages array in chat state:', chatState.messages);
        console.log('Messages array length:', chatState.messages?.length);
        
        // Check if the saved state is recent (within last 2 hours for chat sessions)
        const twoHoursMs = 2 * 60 * 60 * 1000;
        const timeDiff = Date.now() - chatState.timestamp;
        console.log('Time difference from saved state:', timeDiff, 'ms');
        
        if (timeDiff < twoHoursMs) {
          console.log('Restoring chat state from localStorage for patient:', patientId);
          console.log('Restored messages count:', chatState.messages?.length || 0);
          console.log('Messages being restored:', chatState.messages?.map(m => ({ sender: m.sender, text: m.text.substring(0, 50) + '...' })) || []);
          
          // Set the messages directly
          setMessages(chatState.messages || []);
          setHasShownWelcome(chatState.hasShownWelcome || false);
          setJustRestoredState(true); // Set flag to true after successful restoration
          
          console.log('Messages state set to:', chatState.messages);
          console.log('Messages state length after setting:', (chatState.messages || []).length);
          return true;
        } else {
          // Clear old state
          console.log('Clearing old chat state for patient:', patientId, 'age:', timeDiff, 'ms');
          localStorage.removeItem(`chat-state-${patientId}`);
        }
      } else {
        console.log('No saved state found for patient:', patientId);
      }
    } catch (error) {
      console.error('Error loading chat state:', error);
    }
    return false;
  };

  // Start a new chat session for the current patient
  const startNewChat = () => {
    if (!selectedPatient) return;
    
    console.log('Starting new chat session for patient:', selectedPatient);
    
    // Clear any saved chat state for this patient
    localStorage.removeItem(`chat-state-${selectedPatient}`);
    
    // Create a new session ID
    const newSessionId = `web-chat-${Date.now()}`;
    setCurrentSessionId(newSessionId);
    localStorage.setItem(`chat-session-${selectedPatient}`, newSessionId);
    
    // Clear messages and show welcome
    const selectedPatientData = patientsList.find(p => p.id === selectedPatient);
    const patientName = selectedPatientData 
      ? maskPatientName(`${selectedPatientData.firstName} ${selectedPatientData.lastName}`, selectedPatientData.id)
      : 'o paciente';
    const patientFirstName = patientName.split(' ')[0]; // Get first name from masked name
    
    const welcomeMessage = {
      id: Date.now(),
      text: `Olá! Sou a GIA. Como posso ajudar você com questões relacionadas ${patientFirstName}?`,
      sender: 'bot',
      timestamp: new Date().toISOString()
    };
    setMessages([welcomeMessage]);
    setHasShownWelcome(true);
    setJustRestoredState(false); // Reset flag for new session
    
    toast({
      title: 'Nova conversa iniciada',
      description: 'Uma nova sessão de chat foi criada.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const loadPatients = async () => {
    try {
      setLoadingPatients(true);
      setError('');
      console.log('Loading patients...');
      const response = await patients.getAll();
      console.log('Patients response:', response);
      
      // Handle different response structures
      let patientsData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          patientsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          patientsData = response.data.data;
        } else if (response.data.patients && Array.isArray(response.data.patients)) {
          patientsData = response.data.patients;
        }
      }
      
      console.log('Processed patients data:', patientsData);
      setPatientsList(patientsData);
    } catch (error) {
      console.error('Error loading patients:', error);
      setError('Erro ao carregar pacientes. Verifique se está logado.');
      setPatientsList([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadChatHistory = async () => {
    try {
      console.log('Loading chat history for patient:', selectedPatient, 'session:', currentSessionId);
      console.log('Current messages count:', messages.length);
      console.log('Has shown welcome:', hasShownWelcome);
      console.log('Just restored state:', justRestoredState);
      
      // Skip if we just restored state from localStorage
      if (justRestoredState) {
        console.log('Skipping loadChatHistory because state was just restored from localStorage');
        setJustRestoredState(false); // Reset the flag
        return;
      }
      
      // Only load interactions from the current session
      if (!currentSessionId) {
        console.log('No session ID, starting fresh');
        // No session ID means this is a new session, start fresh
        setMessages([]);
        setHasShownWelcome(false);
        return;
      }

      // Check if this is an existing session that should show welcome
      const existingSessionId = localStorage.getItem(`chat-session-${selectedPatient}`);
      if (existingSessionId === currentSessionId && !hasShownWelcome && messages.length === 0) {
        console.log('Showing welcome message for existing session');
        // This is an existing session but no history found, show welcome
        const selectedPatientData = patientsList.find(p => p.id === selectedPatient);
        const patientName = selectedPatientData 
          ? maskPatientName(`${selectedPatientData.firstName} ${selectedPatientData.lastName}`, selectedPatientData.id)
          : 'o paciente';
        const patientFirstName = patientName.split(' ')[0]; // Get first name from masked name
        
        const welcomeMessage = {
          id: Date.now(),
          text: `Olá! Sou a GIA. Como posso ajudar você com questões relacionadas ${patientFirstName}?`,
          sender: 'bot',
          timestamp: new Date().toISOString()
        };
        setMessages([welcomeMessage]);
        setHasShownWelcome(true);
      } else if (messages.length === 0) {
        console.log('No messages found, starting fresh');
        // No history and not an existing session, or welcome already shown
        setMessages([]);
        setHasShownWelcome(false);
      } else {
        console.log('Messages already restored from localStorage, keeping them');
        console.log('Current messages count after restoration:', messages.length);
        // If messages.length > 0, it means chat state was restored from localStorage, so don't override
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      // Don't show error to user, just start with empty chat
      if (messages.length === 0) {
        setMessages([]);
        // Check if this is an existing session
        const existingSessionId = localStorage.getItem(`chat-session-${selectedPatient}`);
        if (existingSessionId === currentSessionId && !hasShownWelcome) {
          // This is an existing session, show welcome message
          const welcomeMessage = {
            id: Date.now(),
            text: "Olá! Sou a GIA, sua consultora virtual especializada em TEA",
            sender: 'bot',
            timestamp: new Date().toISOString()
          };
          setMessages([welcomeMessage]);
          setHasShownWelcome(true);
        } else {
          setHasShownWelcome(false);
        }
      }
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !selectedPatient) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    // Update messages and save immediately with the new array
    setMessages(prev => {
      const updatedMessages = [...prev, userMessage];
      // Save the updated messages immediately
      setTimeout(() => {
        const chatState = {
          patientId: selectedPatient,
          sessionId: currentSessionId,
          messages: updatedMessages,
          hasShownWelcome: hasShownWelcome,
          timestamp: Date.now()
        };
        localStorage.setItem(`chat-state-${selectedPatient}`, JSON.stringify(chatState));
        console.log('Saved chat state after user message - messages:', updatedMessages.length);
        console.log('Messages being saved:', updatedMessages.map(m => ({ sender: m.sender, text: m.text.substring(0, 50) + '...' })));
      }, 0);
      return updatedMessages;
    });
    
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      console.log('Sending message to N8N:', {
        patientId: selectedPatient,
        question: currentMessage,
        sessionId: currentSessionId
      });

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      const response = await fetch('https://dptensor.app.n8n.cloud/webhook/health-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patientId: selectedPatient,
          question: currentMessage,
          sessionId: currentSessionId,
          jwtToken: token
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('N8N response status:', response.status);
      console.log('N8N response data:', data);

      if (data.success) {
        const botMessage = {
          id: Date.now() + 1,
          text: data.response,
          sender: 'bot',
          timestamp: data.timestamp || new Date().toISOString()
        };

        // Update messages and save immediately with the new array
        setMessages(prev => {
          const updatedMessages = [...prev, botMessage];
          // Save the updated messages immediately
          setTimeout(() => {
            const chatState = {
              patientId: selectedPatient,
              sessionId: currentSessionId,
              messages: updatedMessages,
              hasShownWelcome: hasShownWelcome,
              timestamp: Date.now()
            };
            localStorage.setItem(`chat-state-${selectedPatient}`, JSON.stringify(chatState));
            console.log('Saved chat state after bot response - messages:', updatedMessages.length);
            console.log('Messages being saved:', updatedMessages.map(m => ({ sender: m.sender, text: m.text.substring(0, 50) + '...' })));
          }, 0);
          return updatedMessages;
        });
      } else {
        throw new Error(data.error || 'Failed to get response from N8N');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.',
        sender: 'bot',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle patient selection
  const handlePatientChange = (patientId) => {
    console.log('Patient changed to:', patientId);
    console.log('Current selectedPatient before change:', selectedPatient);
    console.log('Current messages count before change:', messagesRef.current.length);
    
    // Save current chat state before switching
    if (selectedPatient && currentSessionId && messagesRef.current.length > 0) {
      console.log('Saving current chat state before switching - messages count:', messagesRef.current.length);
      const chatState = {
        patientId: selectedPatient,
        sessionId: currentSessionId,
        messages: messagesRef.current,
        hasShownWelcome: hasShownWelcome,
        timestamp: Date.now()
      };
      localStorage.setItem(`chat-state-${selectedPatient}`, JSON.stringify(chatState));
      console.log('Saved chat state for patient:', selectedPatient, 'messages:', messagesRef.current.length);
    }
    
    setSelectedPatient(patientId);
    setJustRestoredState(false); // Reset the flag when switching patients
    
    if (patientId) {
      // First, try to load chat state from localStorage to see if there's existing data
      console.log('Attempting to load chat state for patient:', patientId);
      const stateLoaded = loadChatState(patientId);
      console.log('State loaded result:', stateLoaded);
      
      if (stateLoaded) {
        // If we successfully loaded state, use the session ID from the loaded state
        const savedState = localStorage.getItem(`chat-state-${patientId}`);
        if (savedState) {
          const chatState = JSON.parse(savedState);
          console.log('Using session ID from loaded state:', chatState.sessionId);
          setCurrentSessionId(chatState.sessionId);
          localStorage.setItem(`chat-session-${patientId}`, chatState.sessionId);
        }
      } else {
        // Check if there's an existing session for this patient
        const existingSessionId = localStorage.getItem(`chat-session-${patientId}`);
        console.log('Existing session ID for patient:', existingSessionId);
        
        if (existingSessionId) {
          console.log('Found existing session:', existingSessionId);
          setCurrentSessionId(existingSessionId);
          
          // Try to load chat state from localStorage again (in case it was created after session)
          const stateLoadedAgain = loadChatState(patientId);
          console.log('State loaded again result:', stateLoadedAgain);
          
          if (!stateLoadedAgain) {
            // If no saved state, let loadChatHistory handle it
            console.log('No saved state found, letting loadChatHistory handle it');
            setHasShownWelcome(false);
            setMessages([]); // Clear messages so loadChatHistory can set them
          } else {
            console.log('State loaded successfully, messages should be restored');
          }
        } else {
          // Create a new session ID for this patient
          const newSessionId = `web-chat-${Date.now()}`;
          console.log('Creating new session:', newSessionId);
          setCurrentSessionId(newSessionId);
          localStorage.setItem(`chat-session-${patientId}`, newSessionId);
          
          // Only show welcome message for new sessions
          const selectedPatientData = patientsList.find(p => p.id === patientId);
          const patientName = selectedPatientData 
            ? maskPatientName(`${selectedPatientData.firstName} ${selectedPatientData.lastName}`, selectedPatientData.id)
            : 'o paciente';
          const patientFirstName = patientName.split(' ')[0]; // Get first name from masked name
          
          const welcomeMessage = {
            id: Date.now(),
            text: `Olá! Sou a GIA. Como posso ajudar você com questões relacionadas ${patientFirstName}?`,
            sender: 'bot',
            timestamp: new Date().toISOString()
          };
          setMessages([welcomeMessage]);
          setHasShownWelcome(true);
        }
      }
    } else {
      // Clear session when no patient is selected
      console.log('Clearing session');
      setCurrentSessionId(null);
      setMessages([]);
      setHasShownWelcome(false);
    }
  };

  // Memoize the messages list to prevent unnecessary re-renders
  const messagesList = useMemo(() => {
    return messages.map((message) => (
      <ChatMessage key={message.id} message={message} />
    ));
  }, [messages]);

  return (
    <Container maxW="6xl" h="full" p={0}>
      <VStack h="full" spacing={0}>
        {/* Header */}
        <Box
          w="full"
          bg={cardBg}
          borderBottom="1px"
          borderColor={borderColor}
          p={6}
          borderRadius="lg"
          mb={4}
        >
          <HStack spacing={4} align="center">
            <Avatar
              size="md"
              name="GIA"
              bg="brand.500"
              color="white"
              icon={<Icon as={MessageCircle} boxSize={6} />}
            />
            <VStack align="flex-start" spacing={1}>
              <Heading size="lg" color={textColor}>
                GiA chat
              </Heading>
              <Text color="gray.500" fontSize="sm">
                Pergunte informações específicas sobre os pacientes e você receberá respostas específicas considerando o histórico completo do paciente e dará respostas específicas
              </Text>
            </VStack>
          </HStack>
        </Box>

        <Flex w="full" h="full" gap={6}>
          {/* Patient Selection Sidebar */}
          <Box w="300px" bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="semibold" mb={2} color={textColor}>
                  Selecionar Paciente
                </Text>
                <Select
                  value={selectedPatient}
                  onChange={(e) => handlePatientChange(e.target.value)}
                  placeholder="Selecione um paciente"
                  disabled={loadingPatients}
                  size="md"
                  borderRadius="md"
                >
                  {Array.isArray(patientsList) && patientsList
                    .sort((a, b) => {
                      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                      return nameA.localeCompare(nameB, 'pt-BR');
                    })
                    .map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {maskPatientName(`${patient.firstName} ${patient.lastName}`, patient.id)} - {patient.patientId}
                      </option>
                    ))}
                </Select>
                {loadingPatients && (
                  <HStack mt={2} spacing={2}>
                    <Spinner size="sm" />
                    <Text fontSize="sm" color="gray.500">
                      Carregando pacientes...
                    </Text>
                  </HStack>
                )}
                {error && (
                  <Alert status="error" mt={2} borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">
                      {error}
                    </Text>
                  </Alert>
                )}
              </Box>

              {/* Helper Questions Guide */}
              <Box>
                <HStack spacing={2} mb={3}>
                  <Icon as={HelpCircle} color="brand.500" />
                  <Text fontWeight="semibold" color={textColor} fontSize="sm">
                    Exemplos de Perguntas
                  </Text>
                </HStack>
                <Accordion allowToggle size="sm">
                  {helperQuestions.map((category, index) => (
                    <AccordionItem key={index} border="none">
                      <AccordionButton 
                        py={2} 
                        px={3} 
                        borderRadius="md"
                        _hover={{ bg: 'gray.50' }}
                        _expanded={{ bg: 'brand.50' }}
                      >
                        <Box flex="1" textAlign="left">
                          <Text fontSize="xs" fontWeight="medium" color={textColor}>
                            {category.title}
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4} px={3}>
                        <VStack spacing={2} align="stretch">
                          {category.examples.map((example, exampleIndex) => (
                            <Box 
                              key={exampleIndex} 
                              p={2} 
                              bg="gray.50" 
                              borderRadius="md"
                              cursor="pointer"
                              _hover={{ bg: 'gray.100' }}
                              onClick={() => setInputMessage(example)}
                            >
                              <Text fontSize="xs" color="gray.700" lineHeight="1.3">
                                "{example}"
                              </Text>
                            </Box>
                          ))}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Box>

              {selectedPatient && (
                <Box mt={4}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="brand"
                    onClick={startNewChat}
                    leftIcon={<Icon as={MessageCircle} boxSize={4} />}
                  >
                    Nova Conversa
                  </Button>
                </Box>
              )}
            </VStack>
          </Box>

          {/* Chat Area */}
          <Box flex={1} bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} overflow="hidden">
            <VStack h="full" spacing={0} align="stretch">
              {/* Messages Area */}
              <Box
                flex={1}
                p={6}
                overflowY="auto"
                bg={bgColor}
                css={{
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'gray.300',
                    borderRadius: '24px',
                  },
                }}
              >
                <AnimatePresence>
                  {messages.length === 0 ? (
                    <MotionVStack
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      spacing={4}
                      justify="center"
                      align="center"
                      h="full"
                      color="gray.500"
                    >
                      <Icon as={MessageCircle} boxSize={16} />
                      <Text fontSize="lg" fontWeight="medium">
                        Olá! Sou a GIA, sua especialista virtual em TEA.
                      </Text>
                      <Text fontSize="sm" textAlign="center">
                        Aqui ao lado você encontrará exemplos de perguntas que podem inspirar suas dúvidas.
                      </Text>
                    </MotionVStack>
                  ) : (
                    <VStack spacing={0} align="stretch">
                      {messagesList}
                      {isLoading && <TypingIndicator />}
                    </VStack>
                  )}
                </AnimatePresence>
                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area */}
              <Box bg="white" borderTop="1px" borderColor="gray.200" p={6}>
                <Box position="relative" width="100%" maxW="none">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Digite sua pergunta..."
                    disabled={!selectedPatient || isLoading}
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      fontSize: '16px',
                      padding: '20px 60px 20px 20px',
                      borderRadius: '25px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: 'white',
                      resize: 'none',
                      outline: 'none',
                      fontFamily: 'inherit',
                      lineHeight: '1.6',
                      boxSizing: 'border-box',
                      display: 'block',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3182ce';
                      e.target.style.boxShadow = '0 0 0 1px #3182ce';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <Box
                    position="absolute"
                    right="12px"
                    top="50%"
                    transform="translateY(-50%)"
                  >
                    <IconButton
                      onClick={sendMessage}
                      disabled={!inputMessage.trim() || !selectedPatient || isLoading}
                      colorScheme="blue"
                      size="sm"
                      h="40px"
                      w="40px"
                      borderRadius="full"
                      icon={<Icon as={Send} />}
                      aria-label="Enviar mensagem"
                    />
                  </Box>
                </Box>
              </Box>
            </VStack>
          </Box>
        </Flex>
      </VStack>
    </Container>
  );
};

export default Chat; 